import { Direction } from "odyssey-shared";
import { STICK_DEADZONE, clampToRadius, stickDirection } from "./stick-math.ts";

/**
 * The longest a press may last and still be a tap that goes to the world, in milliseconds.
 */
export const STICK_TAP_MS = 200;

/**
 * Whether a pointer of a type may drive the stick: a finger or a pen, never a mouse, whose clicks belong to the world.
 *
 * @param pointerType The `PointerEvent.pointerType` value.
 * @returns True for every type but `mouse`.
 */
export function drivesStick(pointerType: string): boolean {
  return pointerType !== "mouse";
}

/**
 * Where a short press that never left the dead zone was lifted, to be forwarded to the world as a select.
 */
export interface StickTap {
  readonly x: number;
  readonly y: number;
}

/**
 * Where the stick is drawn: its origin and its knob's offset from the origin, in the units the fingers were given in.
 */
export interface StickView {
  readonly active: boolean;
  readonly originX: number;
  readonly originY: number;
  readonly knobX: number;
  readonly knobY: number;
}

/**
 * The virtual stick's state (ADR-061's amendment of 2026-10-31, decision 6): one finger at a time drives it, the first
 * touch places a floating origin, and the offset from there is the eight-way intent. It knows nothing of the DOM or of
 * who owns the keyboard; the mount feeds it pointer positions and lets it go.
 */
export class StickController {
  private pointer: number | null = null;
  private originX = 0;
  private originY = 0;
  private radius = 0;
  private knobX = 0;
  private knobY = 0;
  private sent: Direction = Direction.None;
  private startedAt = 0;
  private lastX = 0;
  private lastY = 0;
  private peak = 0;

  /**
   * Takes a finger as the stick's unless one already is.
   *
   * @param pointer The finger's id.
   * @param x The finger's x.
   * @param y The finger's y.
   * @param radius The stick's radius in the same unit as the positions.
   * @param now The time in milliseconds, for telling a tap from a hold.
   * @returns True when this finger is now the stick's.
   */
  public down(pointer: number, x: number, y: number, radius: number, now = 0): boolean {
    if (this.pointer !== null || !(radius > 0)) {
      return false;
    }
    this.pointer = pointer;
    this.originX = x;
    this.originY = y;
    this.radius = radius;
    this.knobX = 0;
    this.knobY = 0;
    this.sent = Direction.None;
    this.startedAt = now;
    this.lastX = x;
    this.lastY = y;
    this.peak = 0;
    return true;
  }

  /**
   * Moves the stick's finger; any other finger is ignored.
   *
   * @param pointer The finger's id.
   * @param x The finger's x.
   * @param y The finger's y.
   */
  public move(pointer: number, x: number, y: number): void {
    if (pointer !== this.pointer) {
      return;
    }
    const dx = x - this.originX;
    const dy = y - this.originY;
    this.lastX = x;
    this.lastY = y;
    this.peak = Math.max(this.peak, Math.hypot(dx, dy));
    const knob = clampToRadius(dx, dy, this.radius);
    this.knobX = knob.x;
    this.knobY = knob.y;
    this.sent = stickDirection(dx, dy, this.radius, this.sent);
  }

  /**
   * Lifts the stick's finger; any other finger is ignored.
   *
   * @param pointer The finger's id.
   * @param now The time in milliseconds.
   * @returns The place to forward to the world when the press was a tap: under `STICK_TAP_MS` and never past the dead
   *   zone; otherwise null.
   */
  public up(pointer: number, now = 0): StickTap | null {
    if (pointer !== this.pointer) {
      return null;
    }
    const tap = now - this.startedAt < STICK_TAP_MS && this.peak < this.radius * STICK_DEADZONE ? { x: this.lastX, y: this.lastY } : null;
    this.release();
    return tap;
  }

  /**
   * Lets go whatever finger holds the stick: a lift, a cancel, a lost focus or a modal.
   */
  public release(): void {
    this.pointer = null;
    this.knobX = 0;
    this.knobY = 0;
    this.sent = Direction.None;
  }

  /**
   * The intent the stick sends now.
   *
   * @returns `Direction.None` while no finger holds it or the push is inside the dead zone.
   */
  public direction(): Direction {
    return this.pointer === null ? Direction.None : this.sent;
  }

  /**
   * What to draw.
   *
   * @returns The origin and the knob's offset; the origin is 0 while no finger holds the stick.
   */
  public view(): StickView {
    const active = this.pointer !== null;
    return { active, originX: active ? this.originX : 0, originY: active ? this.originY : 0, knobX: this.knobX, knobY: this.knobY };
  }
}
